import { Controller } from "@hotwired/stimulus";

// This controller handles inline editing of ads in the ads table
export default class extends Controller {
  static targets = ["table", "modal", "editForm", "previewModal"];

  connect() {
    // Initialize Bootstrap modals
    this.editModal = new window.bootstrap.Modal(this.modalTarget);
    this.previewModal = new window.bootstrap.Modal(this.previewModalTarget);

    // Set up event listeners for edit, preview buttons and add button
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Add event listeners for edit buttons
    const editButtons = this.tableTarget.querySelectorAll(".edit-ad-btn");
    editButtons.forEach((button) => {
      button.addEventListener("click", (event) => this.openEditModal(event));
    });

    // Add event listeners for preview buttons
    const previewButtons = this.tableTarget.querySelectorAll(".preview-ad-btn");
    previewButtons.forEach((button) => {
      button.addEventListener("click", (event) => this.openPreviewModal(event));
    });

    // Add event listener for the add button
    const addButton = document.getElementById("add-ad-inline-btn");
    if (addButton) {
      addButton.addEventListener("click", () => this.addNewAd());
    }

    // Add event listener for save button in the modal
    const saveButton = this.modalTarget.querySelector("#saveAdChanges");
    if (saveButton) {
      saveButton.addEventListener("click", () => this.saveChanges());
    }
  }

  // Helper method to ensure the image URL is properly formatted
  validateAndFormatImageUrl(url) {
    if (!url || url.trim() === "") {
      return "";
    }

    let formattedUrl = url.trim();

    // If URL doesn't start with http:// or https://, add https://
    if (!formattedUrl.match(/^https?:\/\//i)) {
      formattedUrl = "https://" + formattedUrl;
    }

    return formattedUrl;
  }

  // Helper method to format link URLs with hashtag fallback for empty URLs
  validateAndFormatLinkUrl(url) {
    if (!url || url.trim() === "") {
      // Return hashtag for empty URLs to prevent navigation
      return "#";
    }

    let formattedUrl = url.trim();

    // If URL doesn't start with http:// or https:// or #, add https://
    if (!formattedUrl.match(/^https?:\/\//i) && !formattedUrl.startsWith("#")) {
      formattedUrl = "https://" + formattedUrl;
    }

    return formattedUrl;
  }

  openEditModal(event) {
    event.preventDefault();

    const button = event.currentTarget;
    this.currentAdId = button.getAttribute("data-ad-id");
    this.currentRow = this.tableTarget.querySelector(
      `tr[data-ad-id="${this.currentAdId}"]`
    );

    if (!this.currentRow) return;

    // Find the corresponding form fields from the original form
    this.findAndPopulateEditForm();

    // Show the modal
    this.editModal.show();
  }

  openPreviewModal(event) {
    event.preventDefault();

    const button = event.currentTarget;
    let imageSrc = button.getAttribute("data-image-src") || "";
    const name = button.getAttribute("data-name") || "";
    const link = button.getAttribute("data-link") || "";

    // Format image URL
    imageSrc = this.validateAndFormatImageUrl(imageSrc);

    // Ensure placeholder and error messages are hidden initially
    const errorMsg = document.getElementById("image-load-error");
    const placeholderMsg = document.getElementById("image-preview-placeholder");
    const debugUrl = document.getElementById("debug-image-url");

    if (errorMsg) errorMsg.style.display = "none";
    if (placeholderMsg) placeholderMsg.style.display = "none";

    // Update the preview modal elements
    const previewName = document.getElementById("adPreviewName");
    const previewImage = document.getElementById("adPreviewImage");
    const previewLink = document.getElementById("adPreviewLink");

    if (debugUrl) debugUrl.textContent = imageSrc;

    // Set image with error handling
    if (previewImage) {
      // Always set alt text for accessibility
      previewImage.setAttribute("alt", `${name} Ad Preview`);
      previewImage.style.display = "none"; // Hide initially until load completes

      // Check if image URL is valid and not empty
      if (imageSrc && imageSrc.trim() !== "") {
        // Test image loading
        const testImg = new Image();
        testImg.onload = () => {
          // Image loaded successfully, show it
          previewImage.style.display = "block";
          previewImage.setAttribute("src", imageSrc);
          if (errorMsg) errorMsg.style.display = "none";
          if (placeholderMsg) placeholderMsg.style.display = "none";
        };

        testImg.onerror = () => {
          // Image failed to load
          previewImage.style.display = "none";
          if (errorMsg) errorMsg.style.display = "block";
          if (placeholderMsg) placeholderMsg.style.display = "none";
        };

        // Start image loading test
        testImg.src = imageSrc;
        previewImage.setAttribute("src", imageSrc);
      } else {
        // Handle empty image URL
        previewImage.setAttribute("src", "");
        previewImage.style.display = "none";

        // Show placeholder message
        if (placeholderMsg) {
          placeholderMsg.style.display = "block";
        }
      }
    }

    if (previewName) previewName.textContent = name;
    if (previewLink) {
      // For links, use hashtag as fallback for empty URLs
      const formattedLink = this.validateAndFormatLinkUrl(link);
      previewLink.setAttribute("href", formattedLink);
    }
  }

  findAndPopulateEditForm() {
    if (!this.currentAdId) return;

    // Find the form field set in the original form
    let formIndex = -1;
    const idFields = document.querySelectorAll(
      'form [name*="campaign[ads_attributes]"][name*="[id]"]'
    );

    idFields.forEach((field, index) => {
      if (field.value === this.currentAdId) {
        formIndex = index;
      }
    });

    if (formIndex === -1 && this.currentAdId === "new") {
      // This is a brand new ad, find the most recently added form
      const newFormSections = document.querySelectorAll(
        '[data-new-record="true"]'
      );
      if (newFormSections.length > 0) {
        // Get the last added form
        const formSection = newFormSections[newFormSections.length - 1];
        this.createEditForm(formSection);
      }
    } else if (formIndex !== -1) {
      // Get the form field set
      const formSections = document.querySelectorAll(
        '[data-rails-nested-form-target="target"]'
      );
      if (formSections.length > 0) {
        const formSection = formSections[0].querySelector(
          `tr[data-new-record="false"]:nth-of-type(${formIndex + 1})`
        );
        if (formSection) {
          this.createEditForm(formSection);
        }
      }
    }
  }

  createEditForm(sourceElement) {
    // Clear the edit form
    this.editFormTarget.innerHTML = "";

    // Create input fields based on the current ad data
    const adRow = this.currentRow;
    if (!adRow) return;

    const formFields = `
      <div class="mb-3">
        <label class="form-label">Name</label>
        <input type="text" name="campaign[ads_attributes][${
          this.currentAdId
        }][name]" class="form-control" value="${
      adRow.querySelector(".ad-name").textContent
    }" required>
      </div>
      <div class="mb-3">
        <label class="form-label">Placement</label>
        <select name="campaign[ads_attributes][${
          this.currentAdId
        }][placement]" class="form-select" required>
          ${this.generatePlacementOptions(
            adRow.querySelector(".ad-placement").textContent
          )}
        </select>
      </div>
      <div class="mb-3">
        <label class="form-label">Size</label>
        <select name="campaign[ads_attributes][${
          this.currentAdId
        }][size]" class="form-select" required>
          ${this.generateSizeOptions(
            adRow.querySelector(".ad-size").textContent
          )}
        </select>
      </div>
      <div class="mb-3">
        <label class="form-label">Priority</label>
        <input type="number" name="campaign[ads_attributes][${
          this.currentAdId
        }][priority]" class="form-control" value="${
      adRow.querySelector(".ad-priority").textContent
    }" min="1" max="1000" required>
      </div>
      <div class="mb-3">
        <label class="form-label">Image URL</label>
        <input type="url" name="campaign[ads_attributes][${
          this.currentAdId
        }][image_src]" class="form-control" value="${this.getImageSrc()}" required>
      </div>
      <div class="mb-3">
        <label class="form-label">Link URL</label>
        <input type="text" name="campaign[ads_attributes][${
          this.currentA}][link_href]" class="form-control" value="${this.getLinkHref()}" pattern="(#|https?://\S+)" title="Enter a valid URL or #" required>
      </div>
    `;

    this.editFormTarget.innerHTML = formFields;

    // Add validation to URL fields
    const imageUrlField = this.editFormTarget.querySelector('input[name*="[image_src]"]');
    if (imageUrlField) {
      imageUrlField.addEventListener("blur", (e) => {
        const inputField = e.target;
        if (inputField.value) {
          inputField.value = this.validateAndFormatImageUrl(inputField.value);
        }
      });
    }
    
    const linkUrlField = this.editFormTarget.querySelector('input[name*="[link_href]"]');
    if (linkUrlField) {
      linkUrlField.addEventListener("blur", (e) => {
        const inputField = e.target;
        if (inputField.value && inputField.value !== "#") {
          inputField.value = this.validateAndFormatLinkUrl(inputField.value);
        }
      });
    }
  }

  generatePlacementOptions(currentPlacement) {
    const placements = [["App Home Page", "app_home_page"]];
    return placements
      .map(
        ([label, value]) =>
          `<option value="${value}" ${
            value === currentPlacement ? "selected" : ""
          }>${label}</option>`
      )
      .join("");
  }

  generateSizeOptions(currentSize) {
    const sizes = [["400x100", "400x100"]];
    return sizes
      .map(
        ([label, value]) =>
          `<option value="${value}" ${
            value === currentSize ? "selected" : ""
          }>${label}</option>`
      )
      .join("");
  }

  getImageSrc() {
    const link = this.currentRow.querySelector(".ad-image-src-link");
    return link ? link.getAttribute("data-image-src") : "";
  }

  getLinkHref() {
    const link = this.currentRow.querySelector("a[href]");
    return link ? link.getAttribute("href") : "";
  }

  saveChanges() {
    if (!this.currentRow || !this.currentAdId) return;

    // Find the original form elements
    const modalInputs = this.editFormTarget.querySelectorAll(
      'input:not([type="hidden"]), select, textarea'
    );

    // Find the corresponding form fields in the original form
    let formSection = null;
    const idFields = document.querySelectorAll(
      'form [name*="campaign[ads_attributes]"][name*="[id]"]'
    );

    idFields.forEach((field) => {
      if (field.value === this.currentAdId) {
        // Get the parent form section
        formSection = field.closest(".nested-form-wrapper");
      }
    });

    if (formSection) {
      // Transfer values from modal to the original form
      modalInputs.forEach((input) => {
        if (input.name) {
          const originalInput = formSection.querySelector(
            `[name="${input.name}"]`
          );
          if (originalInput) {
            // Format URL values based on field name
            if (input.name.includes("[image_src]") && input.value) {
              originalInput.value = this.validateAndFormatImageUrl(input.value);
            } else if (input.name.includes("[link_href]") && input.value && input.value !== "#") {
              originalInput.value = this.validateAndFormatLinkUrl(input.value);
            } else {
              originalInput.value = input.value || "";
            }
          }
        }
      });

      // Update the visible table row with new values
      this.updateTableRow();

      // Also update the preview button data attributes
      this.updatePreviewButton();
    }

    // Close the modal
    this.editModal.hide();
  }

  updateTableRow() {
    if (!this.currentRow) return;

    // Update the name field in the table
    const nameField = this.editFormTarget.querySelector('[name*="[name]"]');
    if (nameField) {
      const nameCell = this.currentRow.querySelector(".ad-name");
      if (nameCell) {
        nameCell.textContent = nameField.value;
      }
    }

    // Update other visible fields as needed
    // For example, placement, size, priority could also be updated
    const placementField = this.editFormTarget.querySelector(
      '[name*="[placement]"]'
    );
    if (placementField) {
      const placementCell = this.currentRow.querySelector(
        "td:nth-child(3) .badge"
      );
      if (placementCell) {
        const selectedOption =
          placementField.options[placementField.selectedIndex];
        placementCell.textContent = selectedOption.text;
      }
    }

    const sizeField = this.editFormTarget.querySelector('[name*="[size]"]');
    if (sizeField) {
      const sizeCell = this.currentRow.querySelector("td:nth-child(4)");
      if (sizeCell) {
        sizeCell.textContent = sizeField.value;
      }
    }

    const priorityField = this.editFormTarget.querySelector(
      '[name*="[priority]"]'
    );
    if (priorityField) {
      const priorityCell = this.currentRow.querySelector("td:nth-child(5)");
      if (priorityCell) {
        priorityCell.textContent = priorityField.value;
      }
    }
  }

  updatePreviewButton() {
    if (!this.currentRow) return;

    // Get updated field values
    const nameField = this.editFormTarget.querySelector('[name*="[name]"]');
    const imageSrcField = this.editFormTarget.querySelector(
      '[name*="[image_src]"]'
    );
    const linkHrefField = this.editFormTarget.querySelector(
      '[name*="[link_href]"]'
    );

    // Find the preview button in the current row
    const previewButton = this.currentRow.querySelector(".preview-ad-btn");

    if (previewButton && nameField && imageSrcField && linkHrefField) {
      previewButton.setAttribute("data-name", nameField.value);

      // Format URL values
      const formattedImageSrc = this.validateAndFormatImageUrl(
        imageSrcField.value
      );
      // Use validateAndFormatLinkUrl for links to handle empty URLs with hashtag
      const formattedLinkHref = this.validateAndFormatLinkUrl(linkHrefField.value);

      previewButton.setAttribute("data-image-src", formattedImageSrc);
      previewButton.setAttribute("data-link", formattedLinkHref);
    }
  }

  addNewAd() {
    // Trigger the add action from the nested form controller
    const addButton = document.querySelector(
      '[data-action="click->rails-nested-form#add"]'
    );
    if (addButton) {
      addButton.click();

      // Wait for the DOM to update
      setTimeout(() => {
        // Find all new form sections
        const newForms = document.querySelectorAll('[data-new-record="true"]');
        if (newForms.length > 0) {
          // Get the last (most recently added) form
          const newForm = newForms[newFormSections.length - 1];

          // Scroll to the new form
          newForm.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }
}
